import { FC, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '../../lib';
import stewardReportServices from '../../services/stewardReportServices';
import { StewardReportHeader, StewardReportDetail, MemberSearchResult, JobMemberInfo, CreateStewardReportDetailRequest } from '../../services/dtos';
import { ReportHeaderBar, WorkerAccordionList, ReportSummaryPanel } from '../../lib/components/StewardReports';

export const ReportEditorPage: FC = () => {
  const { headerId } = useParams<{ headerId: string }>();
  const navigate = useNavigate();
  const [header, setHeader] = useState<StewardReportHeader | null>(null);
  const [details, setDetails] = useState<StewardReportDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track which members have been added (even if they don't have detail records yet)
  // Detail records are only created when user clicks Save button for a day
  const [addedMemberIds, setAddedMemberIds] = useState<Set<string>>(new Set());
  // Track local state from all workers for summary calculation
  const [workerLocalStates, setWorkerLocalStates] = useState<Map<string, { localValues: Map<string, string>; rates: { regTimeRate: number; showCallRate: number; mealPenaltyRate: number } }>>(new Map());
  // Track if there are any unsaved changes across all workers
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const svc = new stewardReportServices();
  const isNew = headerId === 'new';
  const isReadOnly = header?.status === 'Submitted';

  useEffect(() => {
    if (isNew) {
      // For new reports, we should redirect to create modal
      // or handle it differently - for now, show error
      setError('Please use "Create New Report" button to start a new report.');
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      setError(null);

      try {
        const [headerData, detailsData] = await Promise.all([
          svc.getHeader(parseInt(headerId!)),
          svc.getDetails(parseInt(headerId!)),
        ]);

        if (!headerData) {
          setError('Report not found.');
          return;
        }

        setHeader(headerData);
        setDetails(detailsData);
      } catch (err: any) {
        console.error('Error fetching report:', err);
        if (err.type === 'RefreshTokenException' || err.responseStatus?.errorCode === '403' || err.responseStatus?.errorCode === '401') {
          navigate('/login');
        } else if (err.responseStatus?.errorCode === '404') {
          setError('Report not found. You may not have access to this report.');
        } else {
          setError('Failed to load report. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [headerId, navigate]);

  const handleHeaderUpdate = (updatedHeader: StewardReportHeader) => {
    setHeader(updatedHeader);
  };

  // Calculate week dates from header
  const weekDates = useMemo(() => {
    if (!header?.reportBeginDate) return [];
    
    const start = new Date(header.reportBeginDate);
    const dates: Date[] = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  }, [header?.reportBeginDate]);

  // Handle worker addition (accepts both MemberSearchResult and JobMemberInfo)
  // Note: We don't create detail records here - they will be created ONLY when the user
  // clicks the Save button for a day. No API calls on cell edits.
  const handleWorkerAdd = async (member: MemberSearchResult | { uniqueMemberId: string; name?: string }) => {
    // Just track that this member has been added - detail records will be created when Save is clicked
    setAddedMemberIds((prev) => new Set(prev).add(member.uniqueMemberId));
  };

  // Helper to normalize dateFor to just the date portion (YYYY-MM-DD)
  const normalizeDateFor = (dateFor: string | undefined): string => {
    if (!dateFor) return '';
    // If it's already just a date (YYYY-MM-DD), return as is
    if (dateFor.length === 10 && dateFor.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateFor;
    }
    // If it has time component, extract just the date part
    return dateFor.split(' ')[0].split('T')[0];
  };

  // Handle day save - saves all fields for a day in one API call
  const handleDaySave = async (
    date: Date,
    uniqueMemberId: string,
    dayData: {
      regTimeHours?: number;
      overTimeHours?: number;
      doubleTimeHours?: number;
      showCallQty?: number;
      mealPenaltyQty?: number;
      regTimeRate?: number;
      showCallRate?: number;
      mealPenaltyRate?: number;
    }
  ) => {
    if (!header) {
      return;
    }

    const dateStr = date.toISOString().split('T')[0];
    const detail = details.find((d) => normalizeDateFor(d.dateFor) === dateStr && d.uniqueMemberId === uniqueMemberId);

    // Build the DTO instance directly using new
    // Include id if detail exists (update), omit if not (create)
    const request = new CreateStewardReportDetailRequest();
    
    // Set required fields
    request.stewardReportId = header.id;
    request.uniqueMemberId = uniqueMemberId;
    request.dateFor = dateStr;
    
    // Set day data fields - always set the values from dayData
    // dayData should always have these properties set (even if 0)
    console.log('ReportEditorPage: dayData received:', dayData);
    request.regTimeHours = dayData.regTimeHours ?? 0;
    request.overTimeHours = dayData.overTimeHours ?? 0;
    request.doubleTimeHours = dayData.doubleTimeHours ?? 0;
    request.showCallQty = dayData.showCallQty ?? 0;
    request.mealPenaltyQty = dayData.mealPenaltyQty ?? 0;

    // Include id if detail exists (for update)
    if (detail) {
      request.id = detail.id;
    }

    // Always include rates - prefer from dayData (current rates from WorkerCard), 
    // then from existing detail, then from first detail for worker
    if (dayData.regTimeRate !== undefined) {
      request.regTimeRate = dayData.regTimeRate;
    } else if (detail?.regTimeRate !== undefined) {
      request.regTimeRate = detail.regTimeRate;
    } else {
      const firstDetailForWorker = details.find((d) => d.uniqueMemberId === uniqueMemberId);
      if (firstDetailForWorker?.regTimeRate !== undefined) {
        request.regTimeRate = firstDetailForWorker.regTimeRate;
      }
    }

    if (dayData.showCallRate !== undefined) {
      request.showCallRate = dayData.showCallRate;
    } else if (detail?.showCallRate !== undefined) {
      request.showCallRate = detail.showCallRate;
    } else {
      const firstDetailForWorker = details.find((d) => d.uniqueMemberId === uniqueMemberId);
      if (firstDetailForWorker?.showCallRate !== undefined) {
        request.showCallRate = firstDetailForWorker.showCallRate;
      }
    }

    if (dayData.mealPenaltyRate !== undefined) {
      request.mealPenaltyRate = dayData.mealPenaltyRate;
    } else if (detail?.mealPenaltyRate !== undefined) {
      request.mealPenaltyRate = detail.mealPenaltyRate;
    } else {
      const firstDetailForWorker = details.find((d) => d.uniqueMemberId === uniqueMemberId);
      if (firstDetailForWorker?.mealPenaltyRate !== undefined) {
        request.mealPenaltyRate = firstDetailForWorker.mealPenaltyRate;
      }
    }

    console.log('ReportEditorPage: DTO before send:', {
      stewardReportId: request.stewardReportId,
      uniqueMemberId: request.uniqueMemberId,
      dateFor: request.dateFor,
      regTimeHours: request.regTimeHours,
      overTimeHours: request.overTimeHours,
      doubleTimeHours: request.doubleTimeHours,
      showCallQty: request.showCallQty,
      mealPenaltyQty: request.mealPenaltyQty,
      regTimeRate: request.regTimeRate,
      showCallRate: request.showCallRate,
      mealPenaltyRate: request.mealPenaltyRate,
      id: request.id,
    });

    try {
      const savedDetail = await svc.upsertDetail(request);
      
        // Update state with server response
        if (detail) {
          // Update existing detail
          setDetails((prevDetails) => 
            prevDetails.map((d) => (d.id === savedDetail.id ? savedDetail : d))
          );
        } else {
          // Add new detail - also remove from addedMemberIds since we now have a real detail
          setDetails((prevDetails) => [...prevDetails, savedDetail]);
          setAddedMemberIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(uniqueMemberId);
            return newSet;
          });
        }
    } catch (err: any) {
      console.error('Error saving day:', err);
      if (err.type === 'RefreshTokenException' || err.responseStatus?.errorCode === '403' || err.responseStatus?.errorCode === '401') {
        navigate('/login');
      }
      // Revert on error by refetching
      try {
        const fetchedDetails = await svc.getDetails(parseInt(headerId!));
        setDetails(fetchedDetails);
      } catch (fetchErr) {
        console.error('Error refetching details:', fetchErr);
      }
      throw err; // Re-throw so the grid can handle the error state
    }
  };

  // Handle worker delete (deletes all details for a worker)
  const handleDetailDelete = async (stewardReportId: number, uniqueMemberId: string) => {
    try {
      await svc.deleteDetail(stewardReportId, uniqueMemberId);
      // Remove all details for this worker from state
      setDetails((prevDetails) => prevDetails.filter((d) => d.uniqueMemberId !== uniqueMemberId));
      // Also remove from addedMemberIds if present
      setAddedMemberIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(uniqueMemberId);
        return newSet;
      });
    } catch (err: any) {
      console.error('Error deleting worker details:', err);
      if (err.type === 'RefreshTokenException' || err.responseStatus?.errorCode === '403' || err.responseStatus?.errorCode === '401') {
        navigate('/login');
      }
      // TODO: Show error toast
    }
  };

  // Handle finalize report
  const handleFinalize = async (headerId: number) => {
    try {
      const finalizedHeader = await svc.finalizeHeader(headerId);
      setHeader(finalizedHeader);
      // Refetch details to ensure we have the latest state
      const fetchedDetails = await svc.getDetails(headerId);
      setDetails(fetchedDetails);
      // TODO: Show success toast
    } catch (err: any) {
      console.error('Error finalizing report:', err);
      if (err.type === 'RefreshTokenException' || err.responseStatus?.errorCode === '403' || err.responseStatus?.errorCode === '401') {
        navigate('/login');
      }
      // TODO: Show error toast
      throw err; // Re-throw so ReportSummaryPanel can handle it
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
        <button
          onClick={() => navigate('/reports')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Reports
        </button>
      </div>
    );
  }

  if (!header) {
    return null;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* Sticky Header Bar */}
      <ReportHeaderBar
        header={header}
        onHeaderUpdate={handleHeaderUpdate}
        isReadOnly={isReadOnly}
      />

      {/* Two-Pane Layout */}
      <div className="flex flex-1 overflow-hidden flex-col md:flex-row md:items-stretch">
        {/* Main Content Pane */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <div className="max-w-7xl mx-auto">
            <WorkerAccordionList
              details={details}
              weekDates={weekDates}
              isReadOnly={isReadOnly}
              jobId={header.jobId}
              addedMemberIds={addedMemberIds}
              stewardReportId={header.id}
              onDaySave={handleDaySave}
              onDetailDelete={handleDetailDelete}
              onWorkerAdd={handleWorkerAdd}
              onWorkerLocalStateChange={setWorkerLocalStates}
              onUnsavedChangesChange={setHasUnsavedChanges}
            />
          </div>
        </div>

        {/* Summary Panel - Right Drawer (Desktop) / Bottom Sheet (Mobile) */}
        <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0 flex flex-col h-96 md:min-h-full max-h-96">
          <ReportSummaryPanel 
            details={details} 
            isReadOnly={isReadOnly}
            workerLocalStates={workerLocalStates}
            weekDates={weekDates}
            headerId={header.id}
            hasUnsavedChanges={hasUnsavedChanges}
            onFinalize={handleFinalize}
          />
        </div>
      </div>
    </div>
  );
};

